/**
 * Domain 3: Spatial & Visual Reasoning (التصور البصري والمكاني)
 * 8 Modular Template Families for 3D mental rotation, nets, reflections, and isometric structures
 */

import { renderCubeNet, renderIsometricCube, renderOptionSvg, DrawShapes } from './svg-builder.js';

export const SPATIAL_TEMPLATES = [
  // 1. Cube Folding Net to 3D Perspective
  {
    id: 'spatial_cube_folding',
    domain: 'spatial_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 45,
    generate(prng, difficulty = 2) {
      // 4 Theme Families: Bold Geometric Symbols, Numbers, Alphabet, Crisp Icons
      const themes = [
        ['●', '▲', '■', '★', '◆', '✚'],
        ['1', '2', '3', '4', '5', '6'],
        ['A', 'B', 'C', 'D', 'E', 'F'],
        ['◈', '▲', '●', '⬢', '★', '✚']
      ];
      const rawTheme = prng.pick(themes);
      const symbols = prng.shuffle(rawTheme);

      // In standard unfolded cross net:
      // Index 0: Top, 1: Left, 2: Front, 3: Right, 4: Bottom, 5: Back
      // Opposite faces that CANNOT meet at a vertex:
      // (0, 4) - Top and Bottom
      // (1, 3) - Left and Right
      // (2, 5) - Front and Back

      // The 8 valid corner triplets where 3 faces meet:
      const validTriplets = [
        [0, 1, 2],
        [0, 2, 3],
        [0, 3, 5],
        [0, 5, 1],
        [4, 1, 2],
        [4, 2, 3],
        [4, 3, 5],
        [4, 5, 1]
      ];

      const validTriplet = prng.pick(validTriplets);
      const correctCube = renderIsometricCube(symbols[validTriplet[0]], symbols[validTriplet[1]], symbols[validTriplet[2]], 110);

      // Distractors must contain an impossible opposite face pair:
      const third1 = prng.pick([1, 2, 3, 5]);
      const invalidCube1 = renderIsometricCube(symbols[0], symbols[4], symbols[third1], 110);

      const third2 = prng.pick([0, 2, 4, 5]);
      const invalidCube2 = renderIsometricCube(symbols[1], symbols[3], symbols[third2], 110);

      const third3 = prng.pick([0, 1, 3, 4]);
      const invalidCube3 = renderIsometricCube(symbols[2], symbols[5], symbols[third3], 110);

      const allCubes = prng.shuffle([
        { svg: correctCube, isCorrect: true },
        { svg: invalidCube1, isCorrect: false },
        { svg: invalidCube2, isCorrect: false },
        { svg: invalidCube3, isCorrect: false }
      ]);

      const correctIndex = allCubes.findIndex(c => c.isCorrect);

      const options = allCubes.map((item, idx) => ({
        id: String.fromCharCode(65 + idx),
        svg: item.svg
      }));

      const netSvg = renderCubeNet(symbols, 240);

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: 'أي من المكعبات المجسمة التالية يمكن تكوينه بدقة عند طي المخطط السطحي الموضح؟',
        questionText_en: 'Which of the following 3D cubes can be correctly formed by folding the given net?',
        stimulusHtml: netSvg,
        options,
        correctIndex,
        explanation_ar: `في المخطط المنبسط، الأوجه المتقابلة لا يمكن أن تظهر معًا في منظر واحد للمكعب. المكعب الصحيح يجمع ثلاثة أوجه متجاورة فقط تلتقي عند رأس مشترك (${symbols[validTriplet[0]]} و ${symbols[validTriplet[1]]} و ${symbols[validTriplet[2]]}).`,
        explanation_en: `In a folded cube, opposite faces can never be visible simultaneously. The correct cube displays 3 adjacent faces meeting at a single vertex (${symbols[validTriplet[0]]}, ${symbols[validTriplet[1]]}, ${symbols[validTriplet[2]]}).`
      };
    }
  },

  // 2. 2D Mental Rotation of Asymmetric Polygon
  {
    id: 'spatial_mental_rotation_2d',
    domain: 'spatial_reasoning',
    supportedDifficulties: [1, 2, 3],
    expectedTimeSeconds: 35,
    generate(prng, difficulty = 2) {
      // Pool of asymmetric polygon shapes
      const asymmetricShapes = [
        '-20,-30 20,-30 20,-10 0,-10 0,10 15,10 15,30 -20,30',
        '-22,-28 8,-28 22,-8 6,-8 22,22 -6,22 -8,8 -22,8',
        '-24,-24 24,-24 24,-6 8,-6 8,14 -10,14 -10,24 -24,24'
      ];
      const basePts = prng.pick(asymmetricShapes);
      const rotTarget = prng.pick([45, 60, 90, 120, 135, 150, 210, 240, 270, 315]);

      const makePoly = (rot, mirrored = false) => (s) => {
        const transform = `rotate(${rot}) ${mirrored ? 'scale(-1, 1)' : ''}`;
        return `<polygon points="${basePts}" fill="rgba(102, 102, 232, 0.25)" stroke="#83C8F0" stroke-width="2.5" transform="${transform}"/>`;
      };

      const stimulusSvg = `<svg class="iq-svg-spatial-stimulus" viewBox="0 0 160 160" xmlns="http://www.w3.org/2000/svg">
        <rect x="0" y="0" width="160" height="160" rx="12" fill="rgba(14, 22, 40, 0.65)" stroke="rgba(255, 255, 255, 0.12)"/>
        <g transform="translate(80, 80)">${makePoly(0, false)(80)}</g>
      </svg>`;

      // Correct option: rotated rotTarget, NOT mirrored
      const correctFn = makePoly(rotTarget, false);

      // Distractors: mirrored versions at various angles
      const distractors = [
        makePoly(rotTarget, true),
        makePoly((rotTarget + 90) % 360, true),
        makePoly((rotTarget + 180) % 360, true)
      ];

      const allFns = prng.shuffle([correctFn, ...distractors]);
      const correctIndex = allFns.indexOf(correctFn);

      const options = allFns.map((fn, idx) => ({
        id: String.fromCharCode(65 + idx),
        svg: renderOptionSvg(fn, 80, `Option ${idx + 1}`)
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: 'أي من الأشكال التالية يمثل الشكل الأصلي بعد تدويره فقط (دون انعكاس أو قلب مرآة)؟',
        questionText_en: 'Which figure represents the original shape after purely planar rotation (without reflection/mirroring)?',
        stimulusHtml: stimulusSvg,
        options,
        correctIndex,
        explanation_ar: `الشكل الصحيح يطابق الشكل الأصلي بتدوير زاوية قدرها ${rotTarget} درجة مع الحفاظ على الاتجاه اليميني للأذرع دون انعكاس مرآتي.`,
        explanation_en: `The correct figure matches the original through a ${rotTarget}° rotation while preserving chirality (no reflection).`
      };
    }
  },

  // 3. Layer Stack Top-Down Perspective (Order of layers)
  {
    id: 'spatial_layer_overlap',
    domain: 'spatial_reasoning',
    supportedDifficulties: [1, 2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      // 3 Overlapping geometric sheets: A (Circle), B (Square), C (Triangle)
      // Visual top-down view: which sheet is on the very top or bottom?
      const colors = ['#83C8F0', '#6666E8', '#10b981'];
      const layers = [
        { name_ar: 'المربع الأزرق', name_en: 'Blue Square', draw: (s) => `<rect x="-30" y="-30" width="70" height="70" rx="4" fill="rgba(59, 130, 246, 0.75)" stroke="#3b82f6" stroke-width="2"/>` },
        { name_ar: 'الدائرة السماوية', name_en: 'Cyan Circle', draw: (s) => `<circle cx="15" cy="0" r="32" fill="rgba(131, 200, 240, 0.75)" stroke="#83C8F0" stroke-width="2"/>` },
        { name_ar: 'المثلث البنفسجي', name_en: 'Purple Triangle', draw: (s) => `<polygon points="-10,-35 40,25 -30,25" fill="rgba(102, 102, 232, 0.85)" stroke="#6666E8" stroke-width="2"/>` }
      ];

      // Shuffle layer stacking order
      const stacking = prng.shuffle([0, 1, 2]); // bottom to top: stacking[0] is bottom, stacking[2] is top
      const topLayerIdx = stacking[2];
      const correctItem = layers[topLayerIdx];

      let inner = '';
      stacking.forEach(idx => {
        inner += layers[idx].draw(100);
      });

      const stimulusSvg = `<svg class="iq-svg-layers" viewBox="0 0 200 160" xmlns="http://www.w3.org/2000/svg">
        <rect x="0" y="0" width="200" height="160" rx="12" fill="rgba(14, 22, 40, 0.65)" stroke="rgba(255, 255, 255, 0.12)"/>
        <g transform="translate(100, 80)">${inner}</g>
      </svg>`;

      const allOptions = prng.shuffle([
        { text_ar: layers[0].name_ar, text_en: layers[0].name_en, isCorrect: topLayerIdx === 0 },
        { text_ar: layers[1].name_ar, text_en: layers[1].name_en, isCorrect: topLayerIdx === 1 },
        { text_ar: layers[2].name_ar, text_en: layers[2].name_en, isCorrect: topLayerIdx === 2 },
        { text_ar: 'جميعها في مستوى واحد', text_en: 'All in the same layer', isCorrect: false }
      ]);

      const correctIndex = allOptions.findIndex(o => o.isCorrect);

      const options = allOptions.map((o, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: o.text_ar,
        text_en: o.text_en
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: 'بالنظر إلى تراكب الأشكال الشفافة في الرسم، أي شكل يقع في الطبقة العليا تمامًا فوق باقي الأشكال؟',
        questionText_en: 'Looking at the overlapping layers, which geometric shape is placed on the uppermost layer covering the others?',
        stimulusHtml: stimulusSvg,
        options,
        correctIndex,
        explanation_ar: `الشكل الموجود في الطبقة العليا هو (${correctItem.name_ar}) لأن حدوده ومساحته تظهر كاملة دون انقطاع وتغطي حواف الأشكال الأخرى.`,
        explanation_en: `The top layer is (${correctItem.name_en}) because its boundary remains completely unbroken and overlaps the edges of the other sheets.`
      };
    }
  },

  // 4. Mirror Reflection across Oblique Axis
  {
    id: 'spatial_mirror_reflection',
    domain: 'spatial_reasoning',
    supportedDifficulties: [1, 2, 3],
    expectedTimeSeconds: 35,
    generate(prng, difficulty = 2) {
      // Shape on left side of a vertical dotted mirror line
      const makeShape = (pts, stroke = '#83C8F0') => `<polygon points="${pts}" fill="rgba(131, 200, 240, 0.25)" stroke="${stroke}" stroke-width="2.5"/>`;

      // An asymmetric polygon: Triangle with a notch or tail
      const origPts = '30,30 70,30 80,60 50,80 30,50';
      // Mirror across vertical line at x = 100: new x' = 200 - x
      // (200-30=170, 200-70=130, 200-80=120, 200-50=150, 200-30=170)
      const mirroredPts = '170,30 130,30 120,60 150,80 170,50';

      const stimulusSvg = `<svg class="iq-svg-mirror" viewBox="0 0 200 120" xmlns="http://www.w3.org/2000/svg">
        <rect x="0" y="0" width="200" height="120" rx="12" fill="rgba(14, 22, 40, 0.65)" stroke="rgba(255, 255, 255, 0.12)"/>
        ${makeShape(origPts)}
        <line x1="100" y1="10" x2="100" y2="110" stroke="currentColor" stroke-width="2" stroke-dasharray="4 4"/>
        <text x="150" y="65" fill="#83C8F0" font-size="24" font-weight="800" text-anchor="middle">?</text>
      </svg>`;

      // Options: A centered version of the mirrored shape vs distractors
      const drawOption = (pts) => (s) => `<g transform="translate(-50, -50)">${makeShape(pts)}</g>`;

      const correctOption = `<polygon points="70,30 30,30 20,60 50,80 70,50" fill="rgba(131, 200, 240, 0.25)" stroke="#83C8F0" stroke-width="2.5"/>`;
      // Distractor 1: identical without reflection
      const distractor1 = `<polygon points="30,30 70,30 80,60 50,80 30,50" fill="rgba(131, 200, 240, 0.25)" stroke="#83C8F0" stroke-width="2.5"/>`;
      // Distractor 2: upside down reflection
      const distractor2 = `<polygon points="70,70 30,70 20,40 50,20 70,50" fill="rgba(131, 200, 240, 0.25)" stroke="#83C8F0" stroke-width="2.5"/>`;
      // Distractor 3: rotated 90
      const distractor3 = `<polygon points="30,70 30,30 60,20 80,50 50,70" fill="rgba(131, 200, 240, 0.25)" stroke="#83C8F0" stroke-width="2.5"/>`;

      const allDrawings = prng.shuffle([
        { svg: correctOption, isCorrect: true },
        { svg: distractor1, isCorrect: false },
        { svg: distractor2, isCorrect: false },
        { svg: distractor3, isCorrect: false }
      ]);

      const correctIndex = allDrawings.findIndex(d => d.isCorrect);

      const options = allDrawings.map((d, idx) => ({
        id: String.fromCharCode(65 + idx),
        svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">${d.svg}</svg>`
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: 'ما هو الشكل الناتج عن انعكاس الشكل الأصلي عبر خط المرآة العمودي المتقطع؟',
        questionText_en: 'Which option represents the exact reflection of the original shape across the vertical dashed mirror line?',
        stimulusHtml: stimulusSvg,
        options,
        correctIndex,
        explanation_ar: 'الانعكاس عبر محور رأسي يعكس إحداثيات المحور الأفقي (اليمين يصبح يسارًا واليسار يصبح يمينًا)، مع بقاء الإحداثي الرأسي كما هو.',
        explanation_en: 'Reflection across a vertical axis reverses horizontal coordinates (left becomes right) while preserving vertical orientation.'
      };
    }
  },

  // 5. Paper Folding & Hole Punching
  {
    id: 'spatial_cut_and_fold',
    domain: 'spatial_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 45,
    generate(prng, difficulty = 2) {
      // Square sheet folded in half vertically, then in half horizontally (quartered), then a hole punched at top-right
      // Unfolded sheet will have 4 holes symmetrical across both axes!
      const stimulusSvg = `<svg class="iq-svg-fold" viewBox="0 0 280 100" xmlns="http://www.w3.org/2000/svg">
        <rect x="0" y="0" width="280" height="100" rx="12" fill="rgba(14, 22, 40, 0.65)" stroke="rgba(255, 255, 255, 0.12)"/>
        <!-- Step 1: full sheet folded in half -->
        <g transform="translate(25, 20)">
          <rect x="0" y="0" width="55" height="55" fill="rgba(102, 102, 232, 0.15)" stroke="currentColor" stroke-width="1.5"/>
          <line x1="27.5" y1="0" x2="27.5" y2="55" stroke="#3b82f6" stroke-width="2" stroke-dasharray="3 3"/>
          <text x="27.5" y="70" class="iq-svg-text-muted" font-size="10" text-anchor="middle">طي رأسي</text>
        </g>
        <text x="95" y="52" fill="#3b82f6" font-size="16">→</text>
        <!-- Step 2: folded again horizontally -->
        <g transform="translate(115, 20)">
          <rect x="0" y="0" width="27.5" height="55" fill="rgba(102, 102, 232, 0.3)" stroke="currentColor" stroke-width="1.5"/>
          <line x1="0" y1="27.5" x2="27.5" y2="27.5" stroke="#3b82f6" stroke-width="2" stroke-dasharray="3 3"/>
          <text x="14" y="70" class="iq-svg-text-muted" font-size="10" text-anchor="middle">طي أفقي</text>
        </g>
        <text x="160" y="52" fill="#3b82f6" font-size="16">→</text>
        <!-- Step 3: punched corner hole -->
        <g transform="translate(185, 20)">
          <rect x="0" y="0" width="27.5" height="27.5" fill="rgba(102, 102, 232, 0.5)" stroke="currentColor" stroke-width="1.5"/>
          <circle cx="20" cy="8" r="4.5" fill="#ef4444" stroke="currentColor" stroke-width="1"/>
          <text x="14" y="50" class="iq-svg-text-muted" font-size="10" text-anchor="middle">تخريم</text>
        </g>
        <text x="235" y="52" fill="#3b82f6" font-size="20">?</text>
      </svg>`;

      // Draw 60x60 square with holes
      const drawUnfolded = (holePositions) => (s) => {
        let out = `<rect x="-30" y="-30" width="60" height="60" rx="3" fill="rgba(102, 102, 232, 0.15)" stroke="currentColor" stroke-width="1.5"/>`;
        holePositions.forEach(pos => {
          out += `<circle cx="${pos.x}" cy="${pos.y}" r="4" fill="#83C8F0"/>`;
        });
        return out;
      };

      // Correct: 4 symmetric holes near the 4 corners
      const correctHoles = [
        { x: 18, y: -18 }, { x: -18, y: -18 },
        { x: 18, y: 18 }, { x: -18, y: 18 }
      ];

      // Distractor 1: 2 holes only
      const distractor1 = [{ x: 18, y: -18 }, { x: 18, y: 18 }];
      // Distractor 2: 4 holes along center axes
      const distractor2 = [{ x: 0, y: -18 }, { x: 0, y: 18 }, { x: -18, y: 0 }, { x: 18, y: 0 }];
      // Distractor 3: 1 single hole
      const distractor3 = [{ x: 18, y: -18 }];

      const allVariants = prng.shuffle([
        { fn: drawUnfolded(correctHoles), isCorrect: true },
        { fn: drawUnfolded(distractor1), isCorrect: false },
        { fn: drawUnfolded(distractor2), isCorrect: false },
        { fn: drawUnfolded(distractor3), isCorrect: false }
      ]);

      const correctIndex = allVariants.findIndex(v => v.isCorrect);

      const options = allVariants.map((v, idx) => ({
        id: String.fromCharCode(65 + idx),
        svg: renderOptionSvg(v.fn, 80, `Option ${idx + 1}`)
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: 'طُويت ورقة مربعة مرتين متتاليتين ثم تم تخريم ثقب دائري كما هو موضح. كيف سيبدو شكل الورقة بعد فتحها بالكامل؟',
        questionText_en: 'A square paper is folded twice and punched with a single hole. How will the paper appear when completely unfolded?',
        stimulusHtml: stimulusSvg,
        options,
        correctIndex,
        explanation_ar: 'الطي لمرتين (رأسيًا ثم أفقيًا) يقسم الورقة إلى 4 طبقات متماثلة؛ فتح الورقة ينشئ 4 ثقوب متناظرة حول محوري التماثل.',
        explanation_en: 'Folding twice (vertically then horizontally) creates 4 quadrants; unfolding mirrors the single punch symmetrically into 4 holes.'
      };
    }
  },

  // 6. 3D Isometric Block Structure Counting
  {
    id: 'spatial_block_counting_3d',
    domain: 'spatial_reasoning',
    supportedDifficulties: [1, 2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      // 3x3 columns with height grid: e.g.:
      // [2, 1]
      // [3, 2]
      // Total count = 2 + 1 + 3 + 2 = 8 cubes
      const h00 = prng.int(1, 3);
      const h01 = prng.int(1, 2);
      const h10 = prng.int(2, 4);
      const h11 = prng.int(1, 3);

      const totalCubes = h00 + h01 + h10 + h11;

      // Draw isometric stack
      const drawBlock = (col, row, heightIdx) => {
        const cx = 110 + (col - row) * 30;
        const cy = 110 + (col + row) * 16 - heightIdx * 20;
        return renderIsometricCube(null, null, null, 36)
          .replace('<svg', `<g transform="translate(${cx - 18}, ${cy - 18})"`)
          .replace('</svg>', '</g>');
      };

      let cubesSvg = '';
      // Render back-to-front, bottom-to-top
      for (let r = 0; r < 2; r++) {
        for (let c = 0; c < 2; c++) {
          const h = (r === 0 && c === 0) ? h00 : (r === 0 && c === 1) ? h01 : (r === 1 && c === 0) ? h10 : h11;
          for (let k = 0; k < h; k++) {
            cubesSvg += drawBlock(c, r, k);
          }
        }
      }

      const stimulusSvg = `<svg class="iq-svg-isometric-structure" viewBox="0 0 220 180" xmlns="http://www.w3.org/2000/svg">
        <rect x="0" y="0" width="220" height="180" rx="12" fill="rgba(14, 22, 40, 0.65)" stroke="rgba(255, 255, 255, 0.12)"/>
        ${cubesSvg}
      </svg>`;

      const correctVal = totalCubes;
      const distractors = [correctVal - 1, correctVal + 1, correctVal + 2, correctVal - 2]
        .filter(v => v > 0 && v !== correctVal).slice(0, 3);

      const allVals = prng.shuffle([correctVal, ...distractors]);
      const correctIndex = allVals.indexOf(correctVal);

      const options = allVals.map((val, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: `${val} مكعبات`,
        text_en: `${val} cubes`,
        value: val
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: 'بافتراض عدم وجود مكعبات معلقة في الهواء (أي أن كل مكعب مرتفع يستند على مكعب تحته)، كم عدد المكعبات الإجمالي في هذا المجسم؟',
        questionText_en: 'Assuming all elevated blocks are supported from beneath (no floating blocks), what is the total number of cubes in this 3D structure?',
        stimulusHtml: stimulusSvg,
        options,
        correctIndex,
        explanation_ar: `بحساب أعمدة المكعبات: العمود الخلفي الأيسر به ${h00}، والخلفي الأيمن به ${h01}، والأمامي الأيسر به ${h10}، والأمامي الأيمن به ${h11}. المجموع = ${totalCubes} مكعبًا.`,
        explanation_en: `Counting column heights: back-left has ${h00}, back-right has ${h01}, front-left has ${h10}, front-right has ${h11}. Total = ${totalCubes} cubes.`
      };
    }
  },

  // 7. Shape Puzzle Assembly (Complete Rectangle/Polygon Fit)
  {
    id: 'spatial_shape_assembly',
    domain: 'spatial_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      // Cutout Geometry Type: 0 = Corner Notch, 1 = Diagonal Corner Bevel, 2 = Center Slot Cutout
      const cutType = prng.pick([0, 1, 2]);
      const totalW = prng.int(130, 160);
      const totalH = prng.int(90, 110);
      const startX = 35;
      const startY = 25;

      let bodyPts, notchSvg, drawPiece, correctFn, distractor1, distractor2, distractor3;
      let expAr = '', expEn = '';

      if (cutType === 0) {
        // Corner Notch (randomized dimensions)
        const nw = prng.int(36, 56);
        const nh = prng.int(32, 50);
        const corner = prng.pick(['tr', 'tl', 'br', 'bl']);

        if (corner === 'tr') {
          bodyPts = `${startX},${startY} ${startX + totalW - nw},${startY} ${startX + totalW - nw},${startY + nh} ${startX + totalW},${startY + nh} ${startX + totalW},${startY + totalH} ${startX},${startY + totalH}`;
          notchSvg = `<rect x="${startX + totalW - nw}" y="${startY}" width="${nw}" height="${nh}" fill="none" stroke="#38bdf8" stroke-width="2" stroke-dasharray="5 4"/><text x="${startX + totalW - nw / 2}" y="${startY + nh / 2 + 6}" fill="#38bdf8" font-size="20" font-weight="900" text-anchor="middle">?</text>`;
        } else if (corner === 'br') {
          bodyPts = `${startX},${startY} ${startX + totalW},${startY} ${startX + totalW},${startY + totalH - nh} ${startX + totalW - nw},${startY + totalH - nh} ${startX + totalW - nw},${startY + totalH} ${startX},${startY + totalH}`;
          notchSvg = `<rect x="${startX + totalW - nw}" y="${startY + totalH - nh}" width="${nw}" height="${nh}" fill="none" stroke="#38bdf8" stroke-width="2" stroke-dasharray="5 4"/><text x="${startX + totalW - nw / 2}" y="${startY + totalH - nh / 2 + 6}" fill="#38bdf8" font-size="20" font-weight="900" text-anchor="middle">?</text>`;
        } else if (corner === 'bl') {
          bodyPts = `${startX},${startY} ${startX + totalW},${startY} ${startX + totalW},${startY + totalH} ${startX + nw},${startY + totalH} ${startX + nw},${startY + totalH - nh} ${startX},${startY + totalH - nh}`;
          notchSvg = `<rect x="${startX}" y="${startY + totalH - nh}" width="${nw}" height="${nh}" fill="none" stroke="#38bdf8" stroke-width="2" stroke-dasharray="5 4"/><text x="${startX + nw / 2}" y="${startY + totalH - nh / 2 + 6}" fill="#38bdf8" font-size="20" font-weight="900" text-anchor="middle">?</text>`;
        } else {
          bodyPts = `${startX + nw},${startY} ${startX + totalW},${startY} ${startX + totalW},${startY + totalH} ${startX},${startY + totalH} ${startX},${startY + nh} ${startX + nw},${startY + nh}`;
          notchSvg = `<rect x="${startX}" y="${startY}" width="${nw}" height="${nh}" fill="none" stroke="#38bdf8" stroke-width="2" stroke-dasharray="5 4"/><text x="${startX + nw / 2}" y="${startY + nh / 2 + 6}" fill="#38bdf8" font-size="20" font-weight="900" text-anchor="middle">?</text>`;
        }

        drawPiece = (w, h, extra) => (s) => {
          let out = `<rect x="${-w / 2}" y="${-h / 2}" width="${w}" height="${h}" rx="3" fill="rgba(56, 189, 248, 0.28)" stroke="#38bdf8" stroke-width="2.5"/>`;
          if (extra === 'tab') {
            out += `<rect x="${-w / 2}" y="${-h / 2 - 12}" width="${w / 2}" height="12" fill="#6666E8" stroke="#38bdf8" stroke-width="2"/>`;
          } else if (extra === 'beveled') {
            out += `<polygon points="${-w / 2},${-h / 2} ${-w / 2 + 14},${-h / 2} ${-w / 2},${-h / 2 + 14}" fill="#0e1526"/>`;
          }
          return out;
        };

        correctFn = drawPiece(nw, nh, 'none');
        distractor1 = drawPiece(nh, nw, 'none'); // Inverted orientation/aspect
        distractor2 = drawPiece(nw, nh, 'tab'); // Extra protruding tab
        distractor3 = drawPiece(nw, nh, 'beveled'); // Missing chamfered corner

        expAr = `القطعة الناقصة هي مستطيل مطابق لأبعاد التجويف المفرغ بدقة (عرض ${nw} وارتفاع ${nh}) دون أي زوائد أو اقتطاعات.`;
        expEn = `The missing piece matches the exact notch dimensions (${nw} wide by ${nh} high) without excess tabs or chamfers.`;
      } else if (cutType === 1) {
        // Diagonal Triangular Corner Cut
        const leg = prng.int(40, 58);
        bodyPts = `${startX},${startY} ${startX + totalW - leg},${startY} ${startX + totalW},${startY + leg} ${startX + totalW},${startY + totalH} ${startX},${startY + totalH}`;
        notchSvg = `
          <polygon points="${startX + totalW - leg},${startY} ${startX + totalW},${startY} ${startX + totalW},${startY + leg}" fill="none" stroke="#38bdf8" stroke-width="2" stroke-dasharray="5 4"/>
          <text x="${startX + totalW - leg * 0.4}" y="${startY + leg * 0.55}" fill="#38bdf8" font-size="19" font-weight="900" text-anchor="middle">?</text>
        `;

        const drawTri = (l, rot, shapeType) => (s) => {
          if (shapeType === 'rect') {
            return `<rect x="${-l / 2}" y="${-l / 2}" width="${l}" height="${l}" fill="rgba(56, 189, 248, 0.28)" stroke="#38bdf8" stroke-width="2.5"/>`;
          }
          const h = l;
          return `<polygon points="${-l / 2},${-h / 2} ${l / 2},${-h / 2} ${l / 2},${h / 2}" fill="rgba(56, 189, 248, 0.28)" stroke="#38bdf8" stroke-width="2.5" transform="rotate(${rot})"/>`;
        };

        correctFn = drawTri(leg, 0, 'tri');
        distractor1 = drawTri(leg, 90, 'tri'); // Rotated 90°
        distractor2 = drawTri(leg, 180, 'tri'); // Inverted 180°
        distractor3 = drawTri(leg * 0.8, 0, 'rect'); // Square instead of triangle

        expAr = `التجويف المفرغ عبارة عن مثلث قائم الزاوية متساوي الساقين بطول ضلع ${leg}، والقطعة المطلوبة يجب أن تكون بنفس اتجاه الزاوية القائمة.`;
        expEn = `The cutout is a right isosceles triangle with leg length ${leg}; the required piece must match the orientation of the right angle.`;
      } else {
        // Center Edge Slot Cutout (U-notch)
        const slotW = prng.int(32, 48);
        const slotD = prng.int(28, 42);
        const midX = startX + totalW / 2;

        bodyPts = `${startX},${startY} ${midX - slotW / 2},${startY} ${midX - slotW / 2},${startY + slotD} ${midX + slotW / 2},${startY + slotD} ${midX + slotW / 2},${startY} ${startX + totalW},${startY} ${startX + totalW},${startY + totalH} ${startX},${startY + totalH}`;
        notchSvg = `
          <rect x="${midX - slotW / 2}" y="${startY}" width="${slotW}" height="${slotD}" fill="none" stroke="#38bdf8" stroke-width="2" stroke-dasharray="5 4"/>
          <text x="${midX}" y="${startY + slotD / 2 + 6}" fill="#38bdf8" font-size="20" font-weight="900" text-anchor="middle">?</text>
        `;

        const drawSlotPiece = (w, d, extra) => (s) => {
          let out = `<rect x="${-w / 2}" y="${-d / 2}" width="${w}" height="${d}" rx="2" fill="rgba(56, 189, 248, 0.28)" stroke="#38bdf8" stroke-width="2.5"/>`;
          if (extra === 't-top') {
            out += `<rect x="${-w * 0.75}" y="${-d / 2 - 8}" width="${w * 1.5}" height="8" fill="#6666E8" stroke="#38bdf8" stroke-width="2"/>`;
          } else if (extra === 'tri-head') {
            out += `<polygon points="${-w / 2},${-d / 2} 0,${-d / 2 - 12} ${w / 2},${-d / 2}" fill="#6666E8" stroke="#38bdf8" stroke-width="2"/>`;
          }
          return out;
        };

        correctFn = drawSlotPiece(slotW, slotD, 'none');
        distractor1 = drawSlotPiece(slotD, slotW, 'none'); // Rotated 90 degrees
        distractor2 = drawSlotPiece(slotW, slotD, 't-top'); // T-slot with wings
        distractor3 = drawSlotPiece(slotW, slotD, 'tri-head'); // Triangle crown

        expAr = `التجويف الأوسط مستطيل بعرض ${slotW} وعمق ${slotD}، والقطعة المناسبة تطابق هذه الأبعاد بالتمام دون أجنحة أو نتوءات.`;
        expEn = `The central cutout is a rectangular slot of width ${slotW} and depth ${slotD}; the matching piece fits seamlessly without extraneous wings.`;
      }

      const stimulusSvg = `<svg class="iq-svg-puzzle-fit" viewBox="0 0 230 155" xmlns="http://www.w3.org/2000/svg">
        <rect x="0" y="0" width="230" height="155" rx="14" fill="rgba(14, 22, 40, 0.7)" stroke="rgba(255, 255, 255, 0.16)" stroke-width="1.5"/>
        <polygon points="${bodyPts}" fill="rgba(59, 130, 246, 0.25)" stroke="#38bdf8" stroke-width="3"/>
        ${notchSvg}
      </svg>`;

      const allVariants = prng.shuffle([
        { fn: correctFn, isCorrect: true },
        { fn: distractor1, isCorrect: false },
        { fn: distractor2, isCorrect: false },
        { fn: distractor3, isCorrect: false }
      ]);

      const correctIndex = allVariants.findIndex(v => v.isCorrect);

      const options = allVariants.map((v, idx) => ({
        id: String.fromCharCode(65 + idx),
        svg: renderOptionSvg(v.fn, 110, `Option ${idx + 1}`)
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: 'أي من القطع التالية يكمل الشكل المفرغ ليكوّن شكلاً هندسياً كاملاً مصمتاً دون زيادة أو نقص؟',
        questionText_en: 'Which of the following puzzle pieces accurately fits into the notch to complete a solid geometric figure?',
        stimulusHtml: stimulusSvg,
        options,
        correctIndex,
        explanation_ar: expAr,
        explanation_en: expEn
      };
    }
  },

  // 8. 3D Silhouette Perspective Match
  {
    id: 'spatial_silhouette_view',
    domain: 'spatial_reasoning',
    supportedDifficulties: [1, 2, 3],
    expectedTimeSeconds: 35,
    generate(prng, difficulty = 2) {
      // 3D Cylinder or Cone viewed from side:
      // Cone from top is circle with dot; cone from side is Triangle!
      // Cylinder from top is circle; cylinder from side is Rectangle!
      const objects = [
        {
          name_ar: 'مخروط ثلاثي الأبعاد',
          name_en: '3D Cone',
          topViewSvg: `<circle cx="50" cy="50" r="30" fill="none" stroke="#3b82f6" stroke-width="2.5"/><circle cx="50" cy="50" r="3" fill="currentColor"/>`,
          correctSideSvg: `<polygon points="50,20 80,80 20,80" fill="rgba(102, 102, 232, 0.4)" stroke="#83C8F0" stroke-width="2.5"/>`,
          distractors: [
            `<rect x="25" y="25" width="50" height="50" fill="rgba(102, 102, 232, 0.4)" stroke="#83C8F0" stroke-width="2"/>`,
            `<circle cx="50" cy="50" r="28" fill="rgba(102, 102, 232, 0.4)" stroke="#83C8F0" stroke-width="2"/>`,
            `<polygon points="25,50 50,20 75,50 50,80" fill="rgba(102, 102, 232, 0.4)" stroke="#83C8F0" stroke-width="2"/>`
          ],
          exp_ar: 'المخروط يبدو كدائرة مع نقطة مركزية من الأعلى، بينما يبدو كمثلث متساوي الساقين عند النظر إليه من الجانب.',
          exp_en: 'A cone appears as a circle with a central apex from above, and as an isosceles triangle from side elevation.'
        }
      ];

      const item = objects[0];

      const stimulusHtml = `
        <div class="iq-elevation-stimulus" role="region" aria-label="المسقط الرأسي">
          <div class="iq-elevation-graphic">
            <svg viewBox="0 0 100 100" width="80" height="80" xmlns="http://www.w3.org/2000/svg">
              <rect x="0" y="0" width="100" height="100" rx="8" fill="rgba(30, 41, 59, 0.15)" stroke="rgba(148, 163, 184, 0.35)"/>
              ${item.topViewSvg}
            </svg>
          </div>
          <div class="iq-elevation-meta">
            <div class="iq-elevation-tag">المسقط الرأسي من الأعلى (Top View)</div>
            <div class="iq-elevation-target">المسقط الجانبي المطلوب: <strong>[ ؟ ]</strong></div>
          </div>
        </div>
      `;

      const allOptions = prng.shuffle([
        { svg: item.correctSideSvg, isCorrect: true },
        { svg: item.distractors[0], isCorrect: false },
        { svg: item.distractors[1], isCorrect: false },
        { svg: item.distractors[2], isCorrect: false }
      ]);

      const correctIndex = allOptions.findIndex(o => o.isCorrect);

      const options = allOptions.map((o, idx) => ({
        id: String.fromCharCode(65 + idx),
        svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">${o.svg}</svg>`
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: 'الشكل الموضح يمثل المسقط الرأسي من الأعلى لجسم ثلاثي الأبعاد. كيف يبدو المسقط الجانبي لنفس الجسم؟',
        questionText_en: 'The diagram shows the top-down elevation of a 3D solid. How does the side elevation of that identical object appear?',
        stimulusHtml,
        options,
        correctIndex,
        explanation_ar: item.exp_ar,
        explanation_en: item.exp_en
      };
    }
  }
];
